import styles from './LandingPage.module.scss';
import { Button, NoData, Textbox, Selectmenu } from 'components/generic';
import images from 'assets/Images';
import { useEffect, useContext, useState, Fragment } from 'react';
import amountFormatter from 'utils/amountFormatter.js';
import OverviewContext from 'store/overview-context';
import { Paynow, CallBackLater, PaymentPlan } from './components/components';
import useRazorpay from 'react-razorpay';
import { sortBy, cloneDeep } from 'lodash';
import Hardship from './components/Hardship/Hardship';
import { arrayBufferToBlob } from 'utils/textFormatter';
import { customerQuestionsOptions, langOptions } from 'config/config';

const LandingPage = props => {
	const overviewContext = useContext(OverviewContext);
	const Razorpay = useRazorpay();
	const [displayDocuments, setDisplayDocuments] = useState();
	const [isOTPVerified, setOTPVerified] = useState(false);
	const [isQNAVerified, setQNAVerified] = useState(false);
	const [displayQNA, setDisplayQNA] = useState(undefined);
	const [qnaValue, setQNAValue] = useState({});
	const [otpValue, setOTPValue] = useState();
	const [resendOtpCounter, setResendOtpCounter] = useState(0);
	const [filterDropdownOptions, setFilterDropdownOptions] = useState(customerQuestionsOptions);

	// razorpay generate order API
	const razorpayOrderAPI = async payload => {
		const { data } = await overviewContext.api({ url: 'payments/v1/razorpay/createOrder', method: 'post', data: payload });
		const obj = { ...payload, key_id: data?.data?.key_id, ts_transactionId: data?.data?.ts_transactionId, orderId: data?.data?.id, ts_currency: data?.data?.currency, c_name: data?.data?.c_name };
		overviewContext?.setCreateObj(obj);
		razorpayPayment(obj);
	};

	// moneytech payment initiate API call
	const moneytechPaymentsAPI = async payload => {
		await overviewContext.api({ url: payload.enablePayTo ? 'payments/v1/payTo/createPaymentAgreement' : 'payments/v1/moneytechPay', method: 'post', data: payload, reset: true, successToaster: true });
		refetchOverview();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// razorpay success API call
	const refetchRazorpaySuccess = async payload => {
		await overviewContext.api({ url: 'payments/v1/razorpay/successPayment', method: 'post', data: payload, reset: true, successToaster: true });
	};

	// refetchOverview
	const refetchOverview = async () => {
		const searchParam = props.location.search;
		overviewContext.setSearchURL(searchParam);
		const { data } = await overviewContext.api({ url: `customer/v1/overview${searchParam}`, method: 'get' });
		const overviewData = data?.data;
		data.data.wf_linkConfiguration.featureList = sortBy(overviewData?.wf_linkConfiguration?.featureList, 'order');
		overviewContext.setOverview(overviewData);
		overviewContext.setLinkValidity(true);
		localStorage.setItem('c_id', overviewData?.c_id);
		document.documentElement.style.setProperty('--primary-color', overviewData?.defaultColor);
		window.scrollTo({ top: 0, behavior: 'smooth' });
		if (overviewData?.c_verificationType === 'QNA') {
			const obj = { ci_id: overviewData?.ci_id, ci_accountNo: overviewData?.ci_accountNo };
			const details = await overviewContext.api({ url: 'customer/v1/QA', method: 'post', data: obj });
			const qna = details?.data?.data;
			if (qna.length > 0) {
				const randomIndex = Math.floor(Math.random() * 2);
				setDisplayQNA(qna?.at().data?.[randomIndex]);
			} else {
				setDisplayQNA(null);
			}
		}
		overviewData?.c_verificationType === 'OTP' && handleSendOTP(overviewData);
		const customerLang = data?.data?.customerInfo?.at()?.ci_preferredLanguage;
		const langContent = langOptions.find(lang => lang.key === (customerLang?.toUpperCase() || 'ENGLISH'));
		if (customerLang && customerLang?.toUpperCase() !== 'ENGLISH') {
			const lang = [{ key: customerLang?.toUpperCase(), value: langContent?.value }];
			overviewContext?.setPreferredLang([...[{ key: 'ENGLISH', value: 'English' }], ...lang]);
			overviewContext?.setSelectedLang(customerLang?.toUpperCase());
		}
		overviewContext?.setLangObj(langContent);
	};

	const textFormatter = el => {
		return el?.replaceAll('_', ' ');
	};

	// payment plan create api
	const paymentPlanAPI = async payload => {
		await overviewContext.api({ url: 'customer/v1/paymentPlan', method: 'post', data: payload, successToaster: true, reset: true });
		refetchOverview();
	};

	// callback later api call
	const callbackLaterAPI = async () => {
		const obj = {
			...overviewContext?.createObj,
			...overviewContext?.callbackLaterObj,
			cbl_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
		};
		await overviewContext.api({ url: 'customer/v1/callBackLater', method: 'post', data: obj, reset: true, successToaster: true });
		refetchOverview();
	};

	useEffect(() => {
		refetchOverview();
	}, []);

	// handle action tab click
	const handleClick = async e => {
		await overviewContext.api({ url: `customer/v1/setCounts?token=${overviewContext?.overviewData?.bd_token}&actionType=${e}`, method: 'get' });
		overviewContext.setTabValue(e);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// initiate payments
	const paymentInitiate = e => {
		e?.ts_paymentGateway === 'c_razorpayCreds' && razorpayOrderAPI(e);
		e?.ts_paymentGateway === 'c_bankDetails' && moneytechPaymentsAPI(e);
	};

	// razorpay popup open
	const razorpayPayment = async razorypayOrder => {
		const options = {
			key: razorypayOrder?.key_id,
			amount: razorypayOrder?.ts_amount,
			currency: razorypayOrder?.bd_currencyCode,
			name: razorypayOrder?.c_name,
			description: razorypayOrder?.c_description,
			image: razorypayOrder?.c_logo,
			order_id: razorypayOrder?.orderId,
			handler: async response => {
				const obj = {
					...razorypayOrder,
					paymentId: response?.razorpay_payment_id,
					signature: response?.razorpay_signature,
					ts_resData: JSON.stringify(response)
				};
				overviewContext?.setCreateObj(obj);
				refetchRazorpaySuccess(obj);
			},
			theme: {
				color: '#3399cc'
			}
		};
		const rzp = new Razorpay(options);
		rzp.on('payment.failed', function (response) {
			alert(response.error.description);
		});
		rzp.open();
	};

	const getLang = key => {
		return overviewContext?.langObj?.content?.[key] || '';
	};

	const actionLabel = e => {
		const field = overviewContext?.overviewData?.[e?.fieldKey]?.at();
		const linkConfig = overviewContext?.overviewData?.wf_linkConfiguration;
		if (e?.key === 'PAY_NOW') {
			return field && field?.ts_status === 'INPROGRESS' ? getLang('PENDING_PAYMENT') : field && field?.ts_status === 'COMPLETE' ? getLang('SUCCESS_PAYMENT') : getLang(e?.key);
		} else if (e?.key === 'PAYMENT_PLAN') {
			if (field) {
				return linkConfig?.paymentPlanConfiguration?.allowEdit ? getLang('PAYMENT_PLAN') : getLang('VIEW_PAYMENT_PLAN');
			} else {
				return getLang(e?.key);
			}
		} else if (e?.key === 'CALLBACK_LATER') {
			if (field) {
				return textFormatter(linkConfig?.callbackLaterConfiguration?.allowEdit ? getLang('CALLBACK_LATER') : getLang('VIEW_CALLBACK_LATER'));
			} else {
				return getLang(e?.key);
			}
		} else if (e?.key === 'HARDSHIP') {
			return linkConfig?.hardshipConfiguration && field ? getLang('VIEW_HARDSHIP') : getLang(e?.key);
		}
	};

	// Action buttons
	const buttons = overviewContext?.overviewData && !overviewContext.isLoading && (
		<>
			{overviewContext?.overviewData?.wf_linkConfiguration?.featureList?.map(el => (
				<div key={el?.key} className={styles.container__btn}>
					<Button
						full={true}
						primary={el?.type === 'primary'}
						disabled={el?.key === 'PAY_NOW' && overviewContext?.overviewData?.transactions?.length}
						light={el?.type === 'light'}
						ghost={el?.type === 'ghost'}
						label={actionLabel(el)}
						onSubmit={() => handleClick(el?.key)}
					/>
				</div>
			))}
		</>
	);

	const hardshipSave = async () => {
		await overviewContext.api({ url: 'customer/v1/hardship', method: 'post', data: overviewContext?.createObj, reset: true, successToaster: true });
		refetchOverview();
	};

	const handleDownloadTemplate = async () => {
		const { data } = await overviewContext.api({ url: `customer/v1/downloadPreview?token=${overviewContext?.createObj?.bd_token}`, method: 'get', reset: true });
		const buffer = data?.data?.template_generatedPDF?.data?.data;
		arrayBufferToBlob(buffer, 'download', 'outstanding_amount.pdf', 'pdf');
	};

	const mainComponent = (
		<>
			{<h4 className={styles.container__info}>{getLang('HI')} {overviewContext?.overviewData?.customerInfo?.[0]?.ci_customerNameUnformatted}{getLang('OUTSTANDING')}</h4>}
			<div className={styles.amount}>
				<p onClick={() => setDisplayDocuments(s => !s)} className={styles.container__amount}>
					{amountFormatter({
						value: overviewContext?.overviewData?.bd_dueAmt,
						currencyCode: overviewContext?.overviewData?.bd_currencyCode
					})}
					<img src={images.downArrow} alt='down' />
				</p>
				{displayDocuments && (
					<div className={styles.documentContainer}>
						<h3>{getLang('IMPORTANT_DOCUMENT')}</h3>
						<p>{getLang('DOWNLOAD_DOCUMENT')}</p>
						<Button primary small label={getLang('DOWNLOAD')} height='40px' onSubmit={handleDownloadTemplate} />
					</div>
				)}
			</div>
			{buttons}
		</>
	);

	const handleOTP = val => {
		setOTPValue(val);
	};

	const handleSendOTP = async data => {
		// send api
		await overviewContext.api({ url: `customer/v1/sendOTP?token=${data?.bd_token}`, method: 'get' });
		setResendOtpCounter(20);
		const interval = setInterval(() => {
			setResendOtpCounter(ps => {
				ps === 1 && clearInterval(interval);
				return ps - 1;
			});
		}, 1000);
	};

	const handleResentOTP = () => {
		setOTPValue('');
		handleSendOTP(overviewContext?.overviewData);
	};

	const handleVerifyOTP = async () => {
		const obj = {
			bd_token: overviewContext?.createObj?.bd_token,
			otp: otpValue && otpValue.trim()
		};
		const result = await overviewContext.api({ url: `customer/v1/verifyOTP?token=${overviewContext?.overviewData?.bd_token}`, method: 'post', data: obj });
		result?.data?.data?.status === 200 && setOTPVerified(true);
	};

	const otpComponent = (
		<div className='row'>
			<div className='col-12'>
				<Textbox mandatory placeholder='Enter the OTP which we have sent to you' type='number' onChange={e => handleOTP(e?.target?.value)} value={otpValue} />
			</div>
			<div className='col-12'>
				{resendOtpCounter > 0 ? (
					<h5>Try sending OTP after {resendOtpCounter}s</h5>
				) : (
					<h5 className={styles.hyperlink} onClick={handleResentOTP}>
						Resend OTP
					</h5>
				)}
			</div>
			<div className='col-12'>
				<Button full={true} primary label='Verify' onSubmit={handleVerifyOTP} disabled={otpValue?.length < 6} />
			</div>
		</div>
	);

	const handleQNA = e => {
		setDisplayQNA(ps => {
			const state = cloneDeep(ps);
			state['answer'] = e;
			return state;
		});
	};

	const handleSubmitQNA = async () => {
		const obj = {
			ci_id: overviewContext?.overviewData?.ci_id,
			ci_accountNo: overviewContext?.overviewData?.ci_accountNo,
			key: displayQNA?.key,
			value: displayQNA?.answer
		};
		const result = await overviewContext.api({ url: 'customer/v1/verifyQA', method: 'post', data: obj });
		result?.data?.data?.status === 200 && setQNAVerified(true);
	};

	const listQNA = (
		<div className='row'>
			<div className='col-12'>
				<h3>{displayQNA?.value || '-'}</h3>
			</div>
			<div className='col-12'>
				<Textbox mandatory placeholder='Please answer' type='text' onChange={e => handleQNA(e?.target?.value)} value={displayQNA?.answer} />
			</div>
			<div className='col-12'>
				<Button full={true} primary label='Submit' onSubmit={handleSubmitQNA} disabled={displayQNA?.answer?.length === 0} />
			</div>
		</div>
	);

	const handleCreateQNA = async () => {
		const data = cloneDeep(qnaValue);
		delete data['totalKeys'];
		const obj = {
			ci_id: overviewContext?.overviewData?.ci_id,
			ci_accountNo: overviewContext?.overviewData?.ci_accountNo,
			data: Object.values(data)?.map(elm => {
				elm.answer = elm?.answer?.trim();
				return elm;
			})
		};
		const result = await overviewContext.api({ url: 'customer/v1/createQA', method: 'post', data: obj });
		result?.data?.data?.status === 200 && setQNAVerified(true);
	};

	const handleEachQNA = (e, idx) => {
		const qna = customerQuestionsOptions.find(el => el.key === e);
		setQNAValue(ps => {
			const state = cloneDeep(ps);
			state['totalKeys'] ??= [];
			state['totalKeys'][idx] = e;
			state[idx] = qna;
			setFilterDropdownOptions(() => {
				const filterOptions = cloneDeep(customerQuestionsOptions);
				const options = filterOptions.filter(el => !state?.totalKeys?.includes(el.key));
				return options;
			});
			return state;
		});
	};

	const handleEachAnswer = (e, idx) => {
		setQNAValue(ps => {
			const state = cloneDeep(ps);
			state[idx].answer = e;
			return state;
		});
	};

	const registerQNA = (
		<div className='row'>
			<h3 className='col-12'> Register Security Questions </h3>
			{[1, 2].map((el, idx) => (
				<Fragment key={el}>
					<div className='col-12'>
						<Selectmenu value={qnaValue[idx] ? [qnaValue[idx]] : ''} placeholder='Select a question' mandatory onChange={e => handleEachQNA(e, idx)} menuOptions={filterDropdownOptions} />
					</div>
					<div className='col-12'>
						<Textbox mandatory placeholder='Answer' type='text' onChange={e => handleEachAnswer(e?.target?.value, idx)} value={qnaValue[idx]?.answer} />
					</div>
				</Fragment>
			))}
			<div className='col-12'>
				<Button full={true} primary label='Submit' onSubmit={handleCreateQNA} disabled={qnaValue['0']?.answer && qnaValue['1']?.answer ? false : true} />
			</div>
		</div>
	);

	const qnaComponent = isQNAVerified ? mainComponent : displayQNA === undefined ? <></> : displayQNA?.value ? listQNA : registerQNA;

	return (
		<>
			{overviewContext.linkIsValid && (
				<>
					{overviewContext.tabValue === 'home' && (
						<div className={styles.container}>
							{overviewContext?.overviewData?.clientLogo && (
								<div className={styles.container__image}>
									<img className={styles.container__img} src={overviewContext?.overviewData?.clientLogo} alt='' />
								</div>
							)}
							{!overviewContext.isLoading && (
								<>
									{overviewContext?.overviewData?.c_verificationType === 'OTP' && (isOTPVerified ? mainComponent : otpComponent)}
									{overviewContext?.overviewData?.c_verificationType === 'QNA' && qnaComponent}
									{!overviewContext?.overviewData?.c_verificationType && mainComponent}
								</>
							)}
						</div>
					)}
					{overviewContext.tabValue === 'PAY_NOW' && <Paynow onPaymentInitiate={paymentInitiate} />}
					{overviewContext.tabValue === 'CALLBACK_LATER' && <CallBackLater onSubmitCallback={callbackLaterAPI} />}
					{overviewContext.tabValue === 'PAYMENT_PLAN' && <PaymentPlan onSubmitPaymentPlan={paymentPlanAPI} />}
					{overviewContext.tabValue === 'HARDSHIP' && <Hardship onHardshipSave={hardshipSave} />}
				</>
			)}

			{!overviewContext.linkIsValid && !overviewContext.isLoading && <NoData title='Entry Restricted' description='This payment link might have expired! Please contact Admin Team.' />}
		</>
	);
};

export default LandingPage;
